/**
 * Short-leg delta for similarity matching — 0.01 precision on |delta|.
 */
export const formatShortDelta = (delta) => {
  if (delta == null || delta === '') return null;
  const num = Number(delta);
  if (!Number.isFinite(num)) return null;
  return (Math.round(Math.abs(num) * 100) / 100).toFixed(2);
};

/**
 * DTE from expiration to reference date (default: today), matching formatters.calculateDTE.
 */
export const computeDTE = (expiration, referenceDate = null) => {
  if (!expiration) return null;
  const ref = referenceDate ? new Date(referenceDate) : new Date();
  const expirationDate = new Date(expiration);
  ref.setHours(0, 0, 0, 0);
  expirationDate.setHours(0, 0, 0, 0);
  const diffTime = expirationDate - ref;
  const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  return Number.isFinite(diffDays) ? diffDays : null;
};

export const verticalWidth = (verticalSpread) => {
  const legs = verticalSpread?.legs;
  if (!legs || legs.length < 2) return null;
  const a = Number(legs[0]?.strike);
  const b = Number(legs[1]?.strike);
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null;
  return Math.round(Math.abs(a - b));
};

const normQty = (q) => {
  const n = Number(q);
  return Number.isFinite(n) && n > 0 ? Math.trunc(n) : 1;
};

/**
 * Similarity key: variant, exact DTEs, exact vertical width & qty,
 * calendar/diagonal short delta at 0.01 precision (SPX-independent).
 */
export const makeSimilarityKey = (row, variant, referenceDate = null) => {
  if (!row || (variant !== 'back' && variant !== 'front')) return '';

  const cal = row.calendar_spread?.legs || [];
  const diag = row.diagonal_spread?.legs || [];
  if (cal.length < 2 || diag.length < 1) return '';

  const ref = referenceDate
    ?? row.cached_at
    ?? row.scan_timestamp
    ?? null;

  const frontDte = computeDTE(cal[0]?.expiration, ref);
  const backDte = computeDTE(cal[1]?.expiration, ref);
  const calDelta = formatShortDelta(cal[0]?.delta);
  const diagDelta = formatShortDelta(diag[0]?.delta);
  const vWidth = verticalWidth(row.vertical_spread);

  if (
    frontDte == null || backDte == null || calDelta == null
    || diagDelta == null || vWidth == null
  ) {
    return '';
  }

  const calQty = normQty(row.calendar_quantity);
  const diagQty = variant === 'front'
    ? normQty(row.diagonal_quantity)
    : 1;

  return [
    variant,
    `fDTE:${frontDte}`,
    `bDTE:${backDte}`,
    `calD:${calDelta}`,
    `diagD:${diagDelta}`,
    `vW:${vWidth}`,
    `calQ:${calQty}`,
    `diagQ:${diagQty}`,
  ].join('|');
};

export const describeSimilarityKey = (key) => {
  if (!key) return '';
  const parts = Object.fromEntries(
    key.split('|').slice(1).map((p) => {
      const idx = p.indexOf(':');
      return idx >= 0 ? [p.slice(0, idx), p.slice(idx + 1)] : [p, ''];
    })
  );
  return [
    parts.fDTE != null ? `Front DTE ${parts.fDTE}` : null,
    parts.bDTE != null ? `Back DTE ${parts.bDTE}` : null,
    parts.calD != null ? `Cal short Δ ${parts.calD}` : null,
    parts.diagD != null ? `Diag short Δ ${parts.diagD}` : null,
    parts.vW != null ? `Vert width ${parts.vW}` : null,
    parts.calQ != null ? `Cal×${parts.calQ}` : null,
    parts.diagQ != null ? `Diag×${parts.diagQ}` : null,
  ].filter(Boolean).join(' · ');
};
